// Top bar: brand, customer switcher, the view switcher, an overall progress bar
// (from the routed customer's session when on a customer route, else the
// aggregate across all the reviewer's customers), the theme toggle, and the
// identity chip + sign-out. Wears the customer design system (doc 26) at
// console density, so the console and the practice's view read as one product.

import { useEffect, useState } from 'react';
import { Link, useMatch } from 'react-router-dom';
import { LogOut, Menu, Moon, Sun, X } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { useMyCustomers, useSession } from '../hooks/queries';
import { currentTheme, toggleTheme } from '../theme';
import { Button } from '../ui/primitives';
import { roleLabel } from '../reviewVocab';
import { CustomerSwitcher } from './CustomerSwitcher';
import { ViewSwitcher, type CustomerView } from './ViewSwitcher';
import { ProgressBar } from './ui';
import { capacityLabel, releasePreview, selectedCapacity } from './capacity';
import { verticalLabel } from '../reviewVocab';
import { PortfolioLink } from '../portfolio/PortfolioLink';
import { TeamLinks } from './TeamLinks';

export function TopBar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { identity, logout, isInternalAdmin } = useAuth();
  const reviewMatch = useMatch('/c/:customerId');
  const firmMatch = useMatch('/firms/:firmId');
  // The notice surface is the same customer, a different view of it. Without
  // this the switcher and the breadcrumb empty out the moment a reviewer
  // opens Notices, which reads as having left the customer entirely.
  const noticesMatch = useMatch('/c/:customerId/notices');
  const noticeMatch = useMatch('/c/:customerId/notices/:noticeId');
  const portalMatch = useMatch('/portal/:customerId');
  // The operator's customer page (BL-344). "/customers/new" is the wizard,
  // not a customer, and must not read as one.
  const opMatchRaw = useMatch('/customers/:customerId');
  const opMatch = opMatchRaw && opMatchRaw.params.customerId !== 'new' ? opMatchRaw : null;
  const customerId =
    reviewMatch?.params.customerId ??
    noticesMatch?.params.customerId ??
    noticeMatch?.params.customerId ??
    portalMatch?.params.customerId ??
    opMatch?.params.customerId;
  const view: CustomerView = portalMatch ? 'customer' : opMatch ? 'overview' : 'review';
  const { data: mine } = useMyCustomers();
  const customer = mine?.find((c) => c.customer_id === customerId);
  const crumbName = customer?.name;
  const hasCustomerSwitcher = new Set((mine ?? []).map((row) => row.customer_id)).size > 1;
  const customerRows = (mine ?? []).filter((row) => row.customer_id === customerId);
  const offeredCapacities = new Set(customerRows.flatMap((row) => row.capacities ?? [row.my_role])
    .filter((role) => role in capacityLabel));
  if (offeredCapacities.has('team')) offeredCapacities.add('practice_preview');
  const viewAvailable = offeredCapacities.size > 1;
  const practiceRoute = !!portalMatch;
  const isPreview = practiceRoute && (selectedCapacity(customerId ?? '') === 'practice_preview' || releasePreview(customerId ?? ''));
  const canChoosePracticeAdmin = customerRows.some((row) => (row.capacities ?? [row.my_role]).includes('customer_admin'));
  const home = practiceRoute && customerId ? `/portal/${customerId}` : '/';
  // The review console names itself. index.html carries only the product name
  // so the customer surface can name itself too (it sets its own).
  useEffect(() => {
    if (!portalMatch) document.title = 'VerityRx — Compliance review';
  }, [portalMatch]);

  const header = (
    <>
    <header className={`cx-topbar${practiceRoute ? ' cx-topbar--practice' : ''}${practiceRoute ? ' cx-topbar--practice-only' : ''}${practiceRoute && !hasCustomerSwitcher && !viewAvailable && !isInternalAdmin ? ' cx-topbar--solo' : ''}${menuOpen ? ' cx-topbar--menu-open' : ''}`}>
      {/* The wordmark is the way back to the review queue. It used to be an inert
          <span>, which left /admin and /c/:id with no route home at all — the only
          controls were the customer switcher (which never goes to the queue) and
          Sign out. */}
      <Link className="cx-topbar__brand" to={home} aria-label="VerityRx home">
        <span className="vx-wordmark">VerityRx</span>
      </Link>
      {practiceRoute && <span className="cx-topbar__mobile-practice" title={crumbName}>{crumbName ?? 'Practice'}</span>}
      <button className="cx-account-menu" type="button" aria-label={menuOpen ? 'Close account menu' : 'Open account menu'} aria-expanded={menuOpen} onClick={() => setMenuOpen((open) => !open)}>
        {menuOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
      </button>
      {practiceRoute && !hasCustomerSwitcher ? <>
        <span className="cx-topbar__sep" aria-hidden="true" />
        <span className="cx-topbar__practice" title={crumbName ?? 'Practice'}>{crumbName ?? 'Practice'}</span>
        {customer?.is_evaluation && <span className="cx-topbar__evaluation">Evaluation</span>}
        {customer?.vertical && <span className="cx-topbar__vertical">{verticalLabel(customer.vertical)}</span>}
      </> : <span className="cx-topbar__sep" aria-hidden="true" />}
      {/* Where you are, for the team: Customers / <this customer>. Hierarchy,
          never history (NN/g) — the trail is the same whichever way you came. */}
      {!practiceRoute && isInternalAdmin && customerId && crumbName && (
        <nav className="cx-crumbs" aria-label="Where you are">
          <Link to="/">Accounts</Link>
          <span className="sep" aria-hidden="true">
            /
          </span>
          <span className="here" title={crumbName}>
            {crumbName}
          </span>
        </nav>
      )}
      {hasCustomerSwitcher && <CustomerSwitcher currentId={customerId} />}
      {hasCustomerSwitcher && customer?.vertical && <span className="cx-topbar__vertical">{verticalLabel(customer.vertical)}</span>}
      {practiceRoute && hasCustomerSwitcher && customer?.is_evaluation && <span className="cx-topbar__evaluation">Evaluation</span>}
      <ViewSwitcher customerId={customerId} current={view} />
      <PortfolioLink />
      {isInternalAdmin && <TeamLinks className="cx-team-links--menu" />}
      <span className="cx-topbar__spacer" />
      {/* Binder progress is the REVIEW surface's measure. On the customer's own
          view it is both meaningless and confusing — they have no binder — and
          on the team's customers list it is a reviewer's number on an
          operator's page. */}
      {view === 'review' && !firmMatch && !noticesMatch && !noticeMatch && !reviewMatch &&
        !isInternalAdmin && (
        <OverallProgress customerId={customerId} />
      )}
      {practiceRoute && isInternalAdmin && customerId && <nav className="cx-account-links" aria-label="Back to the console">
        <Link to={`/customers/${customerId}`}>This customer</Link>
      </nav>}
      <ThemeToggle />
      <div className="cx-identity">
        {/* Show a human identity when available. Dev ids stay in a team-only
            tooltip instead of appearing as customer-facing account names. */}
        <span>
          <span title={isInternalAdmin ? identity?.userId : undefined}>
            {identity?.token ? (identity.email ?? 'Signed in') : 'Signed in'}
          </span>
        </span>
        {!viewAvailable && <span className="role">{roleLabel(practiceRoute && offeredCapacities.size === 1 ? [...offeredCapacities][0] : identity?.role)}</span>}
        <Button variant="ghost" size="sm" icon={<LogOut size={14} aria-hidden="true" />} onClick={logout}>
          Sign out
        </Button>
      </div>
    </header>
    {isPreview && <div id="vx-preview-notice" className="cx-preview-notice" role="status">
      Read-only preview — this is what the practice&apos;s full-access people see.{canChoosePracticeAdmin && <> To make changes, choose View as → The practice&apos;s admin.</>}
    </div>}
    </>
  );
  return isPreview ? <div className="cx-preview-chrome">{header}</div> : header;
}

function ThemeToggle() {
  const [dark, setDark] = useState(() => currentTheme() === 'dark');
  useEffect(() => {
    const obs = new MutationObserver(() => setDark(currentTheme() === 'dark'));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return (
    <button
      type="button"
      className="vx-icon-btn"
      onClick={() => setDark(toggleTheme() === 'dark')}
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={dark ? 'Light mode' : 'Dark mode'}
    >
      {dark ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
    </button>
  );
}

function OverallProgress({ customerId }: { customerId?: string }) {
  const { data: session } = useSession(customerId);
  const { data: customers } = useMyCustomers();

  let pct: number;
  let label: string;

  if (customerId && session) {
    pct = session.rollup.pct;
    label = `This binder · ${session.rollup.total - session.rollup.open}/${session.rollup.total}`;
  } else {
    const list = customers ?? [];
    const total = list.reduce((s, c) => s + c.progress.total, 0);
    const reviewed = list.reduce((s, c) => s + c.progress.reviewed, 0);
    pct = total ? (reviewed / total) * 100 : 0;
    label = `All binders · ${reviewed}/${total}`;
  }

  return (
    <div className="cx-topbar__progress">
      <div className="label">
        <span>{label}</span>
        <span>{Math.round(pct)}%</span>
      </div>
      <ProgressBar pct={pct} slim label={label} />
    </div>
  );
}
